
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { TelegramTokenManager } from './telegram/TelegramTokenManager';
import { SystemBotStatus } from './telegram/SystemBotStatus';
import { TelegramConnectionStatus } from './telegram/TelegramConnectionStatus';
import { WebhookSetupManager } from './telegram/WebhookSetupManager';
import { PriceBotWebhookManager } from './telegram/PriceBotWebhookManager';
import { CoordinatorBotWebhookManager } from './telegram/CoordinatorBotWebhookManager';
import { TelegramNotificationHistory } from './telegram/TelegramNotificationHistory';
import BenchmarkBotManager from './telegram/BenchmarkBotManager';
import CoordinatorBotManager from './telegram/CoordinatorBotManager';
import PaymentBotManager from './telegram/PaymentBotManager';
import { Bot, Settings, History, Zap, CreditCard } from 'lucide-react';

const TelegramSettings: React.FC = () => {
  const { userRole } = useAuth();
  const [activeTab, setActiveTab] = useState('general');

  const isAdmin = userRole === 'admin';
  const canUseBenchmark = ['benchmark', 'benchmark_manager', 'admin'].includes(userRole || '');
  const canUseCoordinator = ['coordinator', 'admin'].includes(userRole || '');
  const canUsePayment = ['admin', 'finance', 'user'].includes(userRole || '');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Telegram Integration</h2>
        <p className="text-muted-foreground">
          Configure and manage Telegram bot integrations for notifications and data entry.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            General
          </TabsTrigger>
          {canUsePayment && (
            <TabsTrigger value="payment" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Payment Bot
            </TabsTrigger>
          )}
          {canUseBenchmark && (
            <TabsTrigger value="benchmark" className="flex items-center gap-2">
              <Bot className="h-4 w-4" />
              Benchmark Bot
            </TabsTrigger>
          )}
          {canUseCoordinator && (
            <TabsTrigger value="coordinator" className="flex items-center gap-2">
              <Bot className="h-4 w-4" />
              Coordinator Bot
            </TabsTrigger>
          )}
          <TabsTrigger value="webhooks" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Webhooks
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Main Telegram Bot</CardTitle>
              <CardDescription>
                Configure the main bot for procurement notifications and general system alerts.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isAdmin && (
                <div className="space-y-4">
                  <TelegramTokenManager />
                  <SystemBotStatus />
                </div>
              )}
              <TelegramConnectionStatus />
            </CardContent>
          </Card>
        </TabsContent>

        {canUsePayment && (
          <TabsContent value="payment" className="space-y-6">
            <PaymentBotManager />
          </TabsContent>
        )}

        {canUseBenchmark && (
          <TabsContent value="benchmark" className="space-y-6">
            <BenchmarkBotManager />
          </TabsContent>
        )}

        {canUseCoordinator && (
          <TabsContent value="coordinator" className="space-y-6">
            <CoordinatorBotManager />
          </TabsContent>
        )}

        <TabsContent value="webhooks" className="space-y-6">
          {isAdmin && (
            <div className="space-y-6">
              <WebhookSetupManager />
              <PriceBotWebhookManager />
              <CoordinatorBotWebhookManager />
            </div>
          )}
          {!isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle>Webhook Management</CardTitle>
                <CardDescription>
                  Only administrators can manage webhook settings.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Contact your administrator to configure webhook settings.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <TelegramNotificationHistory />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TelegramSettings;
